import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import {
  Users, DollarSign, MessageSquare, Image, Video, Settings,
  TrendingUp, Crown, Shield, Ban, Check, RefreshCw, ChevronRight,
  BarChart3, CreditCard, UserCheck, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, transRes, settingsRes] = await Promise.all([
        api.get('/api/admin/stats'),
        api.get('/api/admin/users'),
        api.get('/api/admin/transactions'),
        api.get('/api/admin/settings')
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setTransactions(transRes.data.transactions);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 403) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBanUser = async (userId, isBanned) => {
    try {
      await api.post(`/api/admin/users/${userId}/${isBanned ? 'unban' : 'ban'}`, {});
      fetchData();
    } catch (error) {
      console.error('Failed to ban/unban user:', error);
    }
  };

  const handleUpdateSubscription = async (userId, plan) => {
    try {
      await api.post(`/api/admin/users/${userId}/subscription?plan_id=${plan}`, {});
      fetchData();
    } catch (error) {
      console.error('Failed to update subscription:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-zinc-950">
        <RefreshCw className="h-8 w-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Shield className="h-8 w-8 text-emerald-400" />
              Admin Dashboard
            </h1>
            <p className="text-zinc-400 mt-1">Manage your JAF AI platform</p>
          </div>
          <Button onClick={fetchData} variant="outline" className="border-zinc-700">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="overview" className="data-[state=active]:bg-emerald-600">
            <BarChart3 className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-emerald-600">
            <Users className="h-4 w-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="revenue" className="data-[state=active]:bg-emerald-600">
            <DollarSign className="h-4 w-4 mr-2" />
            Revenue
          </TabsTrigger>
          <TabsTrigger value="settings" className="data-[state=active]:bg-emerald-600">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-zinc-400">Total Users</CardTitle>
                <Users className="h-4 w-4 text-emerald-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats?.overview?.total_users || 0}</div>
                <p className="text-xs text-zinc-500">
                  +{stats?.weekly_activity?.new_users || 0} this week
                </p>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-zinc-400">Total Revenue</CardTitle>
                <DollarSign className="h-4 w-4 text-green-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${stats?.revenue?.total_revenue || 0}</div>
                <p className="text-xs text-zinc-500">
                  {stats?.revenue?.completed_transactions || 0} transactions
                </p>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-zinc-400">Messages</CardTitle>
                <MessageSquare className="h-4 w-4 text-blue-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats?.overview?.total_messages || 0}</div>
                <p className="text-xs text-zinc-500">
                  +{stats?.weekly_activity?.messages || 0} this week
                </p>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-zinc-400">Generated Content</CardTitle>
                <Image className="h-4 w-4 text-purple-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {(stats?.overview?.total_images || 0) + (stats?.overview?.total_videos || 0)}
                </div>
                <p className="text-xs text-zinc-500">
                  {stats?.overview?.total_images || 0} images, {stats?.overview?.total_videos || 0} videos
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Subscription Breakdown */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="h-5 w-5 text-yellow-400" />
                Subscription Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-zinc-800 rounded-lg text-center">
                  <div className="text-3xl font-bold text-zinc-300">{stats?.subscriptions?.free || 0}</div>
                  <div className="text-sm text-zinc-500">Free Users</div>
                </div>
                <div className="p-4 bg-gradient-to-br from-emerald-900/50 to-cyan-900/50 rounded-lg text-center border border-emerald-700/50">
                  <div className="text-3xl font-bold text-emerald-400">{stats?.subscriptions?.pro || 0}</div>
                  <div className="text-sm text-emerald-300">Pro Users</div>
                </div>
                <div className="p-4 bg-gradient-to-br from-purple-900/50 to-pink-900/50 rounded-lg text-center border border-purple-700/50">
                  <div className="text-3xl font-bold text-purple-400">{stats?.subscriptions?.premium || 0}</div>
                  <div className="text-sm text-purple-300">Premium Users</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>User Management</CardTitle>
              <CardDescription>View and manage all platform users</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-2">
                  {users.map((u) => (
                    <div
                      key={u.user_id}
                      className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        {u.picture ? (
                          <img src={u.picture} alt="" className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center">
                            <Users className="h-5 w-5 text-zinc-400" />
                          </div>
                        )}
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {u.name || u.email}
                            {u.banned && (
                              <Badge variant="destructive" className="text-xs">Banned</Badge>
                            )}
                          </div>
                          <div className="text-sm text-zinc-500">{u.email}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right text-sm">
                          <div className="text-zinc-400">
                            {u.stats?.messages || 0} msgs · {u.stats?.images || 0} imgs
                          </div>
                          <Badge className={
                            u.subscription === 'premium' ? 'bg-purple-600' :
                            u.subscription === 'pro' ? 'bg-emerald-600' : 'bg-zinc-600'
                          }>
                            {u.subscription || 'free'}
                          </Badge>
                        </div>
                        
                        <div className="flex gap-2">
                          <select
                            className="bg-zinc-700 border-zinc-600 rounded px-2 py-1 text-sm"
                            value={u.subscription || 'free'}
                            onChange={(e) => handleUpdateSubscription(u.user_id, e.target.value)}
                          >
                            <option value="free">Free</option>
                            <option value="pro">Pro</option>
                            <option value="premium">Premium</option>
                          </select>
                          
                          <Button
                            size="sm"
                            variant={u.banned ? 'default' : 'destructive'}
                            onClick={() => handleBanUser(u.user_id, u.banned)}
                          >
                            {u.banned ? <Check className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Revenue Tab */}
        <TabsContent value="revenue">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-green-400" />
                Payment Transactions
              </CardTitle>
              <CardDescription>All payment transactions</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {transactions.length === 0 ? (
                  <div className="text-center py-10 text-zinc-500">
                    No transactions yet
                  </div>
                ) : (
                  <div className="space-y-2">
                    {transactions.map((t) => (
                      <div
                        key={t.id}
                        className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg"
                      >
                        <div>
                          <div className="font-medium">{t.package_id?.toUpperCase()} Plan</div>
                          <div className="text-sm text-zinc-500">
                            {new Date(t.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="font-bold text-green-400">${t.amount}</div>
                            <Badge className={
                              t.status === 'completed' ? 'bg-green-600' :
                              t.status === 'pending' ? 'bg-yellow-600' : 'bg-red-600'
                            }>
                              {t.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>Platform Settings</CardTitle>
              <CardDescription>Configure platform-wide settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-zinc-800 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">Maintenance Mode</div>
                      <div className="text-sm text-zinc-500">Disable access for non-admins</div>
                    </div>
                    <Badge className={settings?.maintenance_mode ? 'bg-red-600' : 'bg-green-600'}>
                      {settings?.maintenance_mode ? 'ON' : 'OFF'}
                    </Badge>
                  </div>
                </div>
                
                <div className="p-4 bg-zinc-800 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">Guest Access</div>
                      <div className="text-sm text-zinc-500">Allow users without login</div>
                    </div>
                    <Badge className={settings?.guest_access_enabled ? 'bg-green-600' : 'bg-red-600'}>
                      {settings?.guest_access_enabled ? 'ON' : 'OFF'}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-zinc-800 rounded-lg">
                <h3 className="font-medium mb-4">Daily Limits by Plan</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-zinc-400 mb-2">Free</div>
                    <div>Chat: {settings?.daily_limits?.free?.chat || 50}</div>
                    <div>Images: {settings?.daily_limits?.free?.image || 5}</div>
                    <div>Videos: {settings?.daily_limits?.free?.video || 0}</div>
                  </div>
                  <div>
                    <div className="text-emerald-400 mb-2">Pro</div>
                    <div>Chat: Unlimited</div>
                    <div>Images: {settings?.daily_limits?.pro?.image || 100}</div>
                    <div>Videos: {settings?.daily_limits?.pro?.video || 10}</div>
                  </div>
                  <div>
                    <div className="text-purple-400 mb-2">Premium</div>
                    <div>Chat: Unlimited</div>
                    <div>Images: Unlimited</div>
                    <div>Videos: {settings?.daily_limits?.premium?.video || 50}</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDashboard;
